import React from 'react'
import { observer } from 'mobx-react-lite'

import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'

import { Icon, Text } from '@mxenabled/mxui'

import AccountDetailEdit from './AccountDetailEdit'

import { getAccountDetailFields, type AccountField } from '../../../utils/AccountFields'
import { useAccountUiStore } from '../../../hooks/useUiStore'
import { useEvent, useGlobalCopyStore } from '../../../../common'

const AccountDetails = () => {
  const widgetProfile = window.app?.config

  const { onEvent } = useEvent()
  const { selectedAccount: account } = useAccountUiStore()
  const { accounts: accountsCopy } = useGlobalCopyStore()

  const fields = getAccountDetailFields(account, accountsCopy)
  const canEdit =
    account.member_is_managed_by_user ||
    (!account.member_is_managed_by_user && widgetProfile.enable_renaming_home_accounts)

  const [showEditDialog, setShowEditDialog] = React.useState(false)
  const [fieldToEdit, setFieldToEdit] = React.useState<AccountField>(fields[0])

  const displayableFields = fields.filter(
    (field) => canEdit || account[field.name] || account[field.name] === 0,
  )

  return (
    <>
      {displayableFields.map((field) => {
        const hasValue = account[field.name] || account[field.name] === 0
        const isLast = displayableFields.indexOf(field) === displayableFields.length - 1

        const content = (
          <ListItemText
            disableTypography={true}
            primary={<Text sx={{ fontSize: 11 }}>{field.label}</Text>}
            secondary={
              <Text bold={true} sx={{ fontSize: 15 }}>
                {hasValue
                  ? (field.valueFormatter(account[field.name]) as string)
                  : field.placeholder}
              </Text>
            }
          />
        )

        return (
          <React.Fragment key={field.name}>
            {canEdit ? (
              <ListItem
                sx={{
                  backgroundColor: 'background.paper',
                  px: 0,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    left: isLast ? 0 : 24,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    setFieldToEdit(field)
                    setShowEditDialog(true)
                    if (field?.event) onEvent(field.event, { account_guid: account.guid })
                  }}
                >
                  {content}
                  <Icon name="chevron_right" />
                </ListItemButton>
              </ListItem>
            ) : (
              hasValue && (
                <ListItem
                  sx={{
                    backgroundColor: 'background.paper',
                    px: 24,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      left: isLast ? 0 : 24,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  {content}
                </ListItem>
              )
            )}
          </React.Fragment>
        )
      })}
      {showEditDialog && fieldToEdit && (
        <AccountDetailEdit
          field={fieldToEdit}
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
        />
      )}
    </>
  )
}

export default observer(AccountDetails)
